"use client"

import { useMemo } from "react"
import { ScrollArea } from "@/components/ui/scroll-area"
import type { GetContractResponseContent } from "src/types/contract.types"
import { projectExtractedEvidence } from "./extracted-evidence"
import { CitationCard } from "./CitationCard"

interface ExtractedEvidencePanelProps {
  contract?: GetContractResponseContent | null
  /**
   * Fired when the user clicks a citation's "Source: page N". Receives the
   * 1-based page number. Omit to render page footers as non-clickable text.
   */
  onJumpToPage?: (page: number) => void
}

/**
 * The evidence surface for the REAL contract-analysis route: every fact the
 * analyzer extracted, grouped by EQ card, each row a CitationCard carrying
 * its label, value, verbatim snippet, and page citation.
 *
 * Reads the per-fact citations via projectExtractedEvidence — a typed
 * projection over the contract's eqCardsRaw envelope (cast at the boundary,
 * NO api-model bump). When there are no facts (e.g. demo contracts, or an
 * analysis that produced no citations) it renders an honest empty state.
 */
export function ExtractedEvidencePanel({
  contract,
  onJumpToPage,
}: ExtractedEvidencePanelProps) {
  const groups = useMemo(() => projectExtractedEvidence(contract), [contract])

  if (!contract) {
    return (
      <ScrollArea className="flex-1">
        <div className="px-4 py-4">
          <p className="text-sm text-muted-foreground">
            No contract data available.
          </p>
        </div>
      </ScrollArea>
    )
  }

  if (groups.length === 0) {
    return (
      <ScrollArea className="flex-1">
        <div className="px-4 py-4">
          <p className="text-sm text-muted-foreground">
            No extracted evidence for this contract yet.
          </p>
        </div>
      </ScrollArea>
    )
  }

  return (
    <ScrollArea className="flex-1">
      <div className="space-y-6 px-4 py-4">
        {groups.map((group) => (
          <section key={group.cardKey} className="space-y-3">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {group.cardLabel}
            </h3>
            <div className="space-y-3">
              {group.facts.map((fact, index) => (
                <CitationCard
                  key={`${group.cardKey}-${index}`}
                  fact={fact}
                  onJumpToPage={onJumpToPage}
                />
              ))}
            </div>
          </section>
        ))}
      </div>
    </ScrollArea>
  )
}
